import { SWIMLANE_COLOR_HEX_PATTERN } from "@bliks-io/contracts";
/** Same rule the API enforces (`SwimlaneColorSchema`), so the picker's
 *  Apply gating can never accept a value the server would reject. */ export function isLaneColorHex(value) {
    return SWIMLANE_COLOR_HEX_PATTERN.test(value);
}
function parseHex(hex) {
    return [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16)
    ];
}
function mix(rgb, target, amount) {
    return [
        Math.round(rgb[0] + (target - rgb[0]) * amount),
        Math.round(rgb[1] + (target - rgb[1]) * amount),
        Math.round(rgb[2] + (target - rgb[2]) * amount)
    ];
}
function toHex(rgb) {
    return `#${rgb.map((c)=>c.toString(16).padStart(2, "0")).join("")}`;
}
/**
 * Derive the three swimlane tones (fill, border, label strip) from one base
 * hex color. Returns `null` for malformed input so callers can fall back to
 * the built-in per-laneType defaults.
 *
 * Reproduces the built-in defaults exactly when `base` is a default border
 * tone: `#BFDBFE` → labelBg `#eff6ff`, bg `rgba(239, 246, 255, 0.6)`.
 */ export function deriveLaneTones(base) {
    if (!base || !isLaneColorHex(base)) return null;
    const baseRgb = parseHex(base);
    const labelRgb = mix(baseRgb, 255, 0.75);
    const luminance = (0.299 * baseRgb[0] + 0.587 * baseRgb[1] + 0.114 * baseRgb[2]) / 255;
    // A near-white base would make the border invisible against the canvas, so
    // darken it slightly instead of using the base verbatim.
    const borderRgb = luminance > 0.92 ? mix(baseRgb, 0, 0.15) : baseRgb;
    return {
        bg: `rgba(${labelRgb[0]}, ${labelRgb[1]}, ${labelRgb[2]}, 0.6)`,
        border: toHex(borderRgb),
        labelBg: toHex(labelRgb)
    };
}
/**
 * Picker palette for user-selectable lane base colors — nine Tailwind-200
 * pastels, harmonious with the built-in lane defaults.
 */ export const SWIMLANE_COLOR_PALETTE = [
    "#BFDBFE",
    "#DDD6FE",
    "#A7F3D0",
    "#FED7AA",
    "#FECDD3",
    "#FDE68A",
    "#99F6E4",
    "#FBCFE8",
    "#E5E7EB"
];
const DEFAULT_LANE_TONES = {
    department: {
        bg: "rgba(239, 246, 255, 0.6)",
        border: "#BFDBFE",
        labelBg: "#EFF6FF"
    },
    team: {
        bg: "rgba(243, 232, 255, 0.6)",
        border: "#DDD6FE",
        labelBg: "#F3E8FF"
    },
    system: {
        bg: "rgba(236, 253, 245, 0.6)",
        border: "#A7F3D0",
        labelBg: "#ECFDF5"
    },
    external: {
        bg: "rgba(255, 247, 237, 0.6)",
        border: "#FED7AA",
        labelBg: "#FFF7ED"
    },
    unassigned: {
        bg: "rgba(249, 250, 251, 0.6)",
        border: "#E5E7EB",
        labelBg: "#F9FAFB"
    }
};
/**
 * The one seam for lane coloring: a user-picked base color wins, malformed or
 * absent values fall back to the built-in per-laneType defaults.
 */ export function resolveLaneTones(laneType, color) {
    return deriveLaneTones(color) ?? DEFAULT_LANE_TONES[laneType];
}

//# sourceMappingURL=laneColorTones.js.map